<template>
  <div
    class="w-full transition-all duration-300 ease-in-out flex items-end gap-1"
    :class="offsetClass"
  >
    <div
      class="px-2 w-full border rounded-lg"
      :class="[
        innerClass,
        showReplyBox && showSelfAssignBanner ? 'pt-2 pb-3' : 'py-3',
      ]"
      @click="!isExpanded && expandMessage()"
      @contextmenu="openContextMenu"
    >
      <div
        v-if="showReplyBox && showSelfAssignBanner"
        class="w-full py-[10px] mb-2 bg-[#DBEAFE] text-gray-800 text-sm text-center rounded-lg"
      >
        <span>{{ $t('CONVERSATION.NOT_ASSIGNED_TO_YOU') }}</span>
        <span
          class="text-[#2563EB] px-3 py-1.5 cursor-pointer rounded-[4px] hover:bg-[#c4ddff]"
          @click="onClickSelfAssign"
        >
          {{ $t('CONVERSATION.ASSIGN_TO_ME') }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <span
          v-if="!isLastNonActivityMessage && !isMessageDeleted"
          class="material-symbols-outlined text-gray-500 text-[24px]/[24px] flex border border-transparent hover:bg-gray-100 rounded-[4px] cursor-pointer transition-all duration-150"
          :class="{
            'rotate-90': isExpanded && !isRTLView,
            '-rotate-90': isExpanded && isRTLView,
          }"
          @click.stop="toggleExpandMesssage"
        >
          {{ isRTLView ? 'keyboard_arrow_left' : 'keyboard_arrow_right' }}
        </span>
        <div
          class="flex flex-col flex-1 gap-1 ltr:pr-1 rtl:pl-1"
          :class="
            isMessageDeleted || isLastNonActivityMessage
              ? 'ltr:pl-1 rtl:pr-1'
              : ''
          "
        >
          <email-message-header
            :sender="sender"
            :receiver-detail-tooltip="receiverDetailTooltip"
            :receiver-name="receiverName"
            :relative-date="relativeDate(messageData.created_at)"
            :hour-stamp="hourStamp"
            :delay-hours="messageData.delay_hours"
            :scheduled-date="scheduledDate"
            :variant="isIncoming ? 'incoming' : 'outgoing'"
            :message-id="messageData.id"
            :has-private-note="!!privateNotes.length"
          >
            <template v-if="shouldShowContextMenu" #contextMenu>
              <div
                :class="
                  isExpanded ? 'context-menu-wrap h-6 flex items-center' : 'w-0'
                "
              >
                <context-menu
                  :context-menu-position="contextMenuPosition"
                  :is-open="showContextMenu"
                  :d99d-enables="d99dEnables"
                  :enabled-options="contextMenuEnabledOptions"
                  :message="messageData"
                  :hide-translate-option="true"
                  :show-trigger-button="isExpanded"
                  class="ltr:ml-2 rtl:mr-2 h-6"
                  @open="openContextMenu"
                  @close="closeContextMenu"
                  @remoteAssignFile="handleRemoteAssignFile"
                  @d99dSelect="handleD99dSelection"
                  @replyTo="handleReplyTo"
                />
              </div>
            </template>
            <template #custom-tag>
              <div
                v-if="isLastIncomingMessage"
                class="bg-white w-fit flex rounded-lg"
              >
                <span class="semantic-tag small tag-primary-05">
                  {{ $t('COMMON.LATEST') }}
                </span>
              </div>
              <div v-if="isMessageDeleted" class="w-fit flex rounded-lg">
                <span class="semantic-tag small tag-secondary-06">
                  {{ $t('COMMON.DELETED') }}
                </span>
              </div>
            </template>
          </email-message-header>
        </div>
      </div>
      <div class="mt-2 px-1">
        <div
          v-if="isExpanded"
          class="message-content overflow-hidden"
          :class="{ 'leading-6': !isIncoming }"
        >
          <div
            ref="emailContentHost"
            v-shadow-html="{
              html: messageContent,
              styles: shouldShowFullEmailContent
                ? basicEmailCss
                : hidingEmailQuote,
            }"
          />
        </div>
        <div
          v-else
          class="message-content-preview line-clamp-2 text-base text-gray-700 wrap-anywhere break-all overflow-hidden"
        >
          <template v-if="isIncoming">{{ plainTextContent }}</template>
          <p v-else class="text-base mb-0" style="word-spacing: 0">
            {{ plainTextContent }}
          </p>
        </div>
        <email-message-attachments
          v-if="hasAttachment"
          :attachments="attachments"
          :variant="isExpanded ? 'large' : 'small'"
          class="mt-3"
        />
        <email-message-actions
          v-if="isExpanded && !showReplyBox && shouldShowContextMenu"
          class="mt-4"
          @reply="handleReplyTo"
          @reply-all="handleReplyAll"
          @forward="handleForwardTo"
          @note="handleNote"
        />
        <div v-if="privateNotes.length && isExpanded" class="mt-4 space-y-2">
          <email-message-private-note
            v-for="privateNote in privateNotes"
            :key="privateNote.id"
            :message-data="privateNote"
          />
        </div>
        <email-message-reply-box
          v-if="showReplyBox"
          class="mt-4"
          :is-showing.sync="showReplyBox"
          :conversation-id="currentChat.id"
          :popout-reply-box.sync="isPopoutReplyBox"
          :message-data="messageData"
          @update:showSelfAssignBanner="showSelfAssignBanner = $event"
          @click="showPopoutReplyBox"
          @forward="handleForwardTo"
        />
      </div>
    </div>
    <span
      v-if="isFailed"
      v-tooltip="externalError"
      class="material-symbols-outlined text-[#EF4444] cursor-default"
    >
      info
    </span>
    <span
      v-if="isPending"
      class="rounded-full overflow-hidden flex items-center justify-center w-6 h-6 shrink-0"
    >
      <svg
        class="animate-spin"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style="transform-origin: 50% 55%"
      >
        <path d="M12.0005 21.8458C13.18 21.8458 14.2851 21.6227 15.3157 21.1766C16.3466 20.7304 17.2453 20.124 18.012 19.3573C18.7787 18.5907 19.3851 17.6919 19.8312 16.6611C20.2774 15.6304 20.5005 14.5253 20.5005 13.3458H19.0005C19.0005 15.2958 18.3213 16.95 16.963 18.3083C15.6047 19.6666 13.9505 20.3458 12.0005 20.3458C10.0505 20.3458 8.39632 19.6666 7.03799 18.3083C5.67965 16.95 5.00049 15.2958 5.00049 13.3458C5.00049 11.3958 5.67965 9.74165 7.03799 8.38332C8.39632 7.02498 10.0505 6.34581 12.0005 6.34581H12.266L10.6775 7.93431L11.7312 9.01881L15.1542 5.58631L11.712 2.15356L10.6582 3.23806L12.266 4.84581H12.0005C10.821 4.84581 9.71591 5.0689 8.68524 5.51507C7.65441 5.96123 6.75565 6.56765 5.98899 7.33431C5.22232 8.10098 4.61591 8.99973 4.16974 10.0306C3.72357 11.0612 3.50049 12.1663 3.50049 13.3458C3.50049 14.5253 3.72357 15.6304 4.16974 16.6611C4.61591 17.6919 5.22232 18.5907 5.98899 19.3573C6.75565 20.124 7.65441 20.7304 8.68524 21.1766C9.71591 21.6227 10.821 21.8458 12.0005 21.8458Z" fill="#6B7280"/>
      </svg>
    </span>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { MESSAGE_TYPE, MESSAGE_STATUS } from 'shared/constants/messages';
import alertMixin from 'shared/mixins/alertMixin';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import ContextMenu from '../MessageContextMenu.vue';
import attributeMixin from 'dashboard/mixins/attributeMixin';
import timeMixin from 'dashboard/mixins/time';
import EmailMessageHeader from './EmailMessageHeader.vue';
import EmailMessageActions from './EmailMessageActions.vue';
import EmailMessageAttachments from './EmailMessageAttachments.vue';
import EmailMessagePrivateNote from './EmailMessagePrivateNote.vue';
import EmailMessageReplyBox from './EmailMessageReplyBox.vue';
import hidingEmailQuote, { basicEmailCss } from './hideEmailQuoteCss';
import rtlMixin from 'shared/mixins/rtlMixin';

export default {
  components: {
    ContextMenu,
    EmailMessageHeader,
    EmailMessageActions,
    EmailMessageAttachments,
    EmailMessagePrivateNote,
    EmailMessageReplyBox,
  },
  mixins: [attributeMixin, timeMixin, alertMixin, hasPermissionMixin, rtlMixin],
  inject: [
    'allMessagesExpanded',
    'replyingToMessageId',
    'setReplyingToMessageId',
    'latestIncomingMessageId',
    'lastNonActivityMessageId',
  ],
  props: {
    messageData: {
      type: Object,
      required: true,
    },
    messageContent: {
      type: String,
      required: true,
    },
    privateNotes: {
      type: Array,
      default: () => [],
    },
    deleteable: {
      type: Boolean,
      default: false,
    },
    assignable: {
      type: Boolean,
      default: false,
    },
    d99dFeatures: {
      type: Object,
      default: () => ({}),
    },
    inboxSupportsReplyTo: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showSelfAssignBanner: false,
      isExpanded: false,
      isPopoutReplyBox: false,
      showReplyBox: false,
      showContextMenu: false,
      contextMenuPosition: {},
      d99dEnables: { empty: true },
      hidingEmailQuote,
      basicEmailCss,
      contentFullyHiddenByQuote: false,
    };
  },
  computed: {
    shouldShowFullEmailContent() {
      return this.contentAttributes.forwarded || this.contentFullyHiddenByQuote;
    },
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
    }),
    isIncoming() {
      return this.messageData.message_type === MESSAGE_TYPE.INCOMING;
    },
    inbox() {
      const { inbox_id: inboxId } = this.currentChat;
      return this.$store.getters['inboxes/getInbox'](inboxId);
    },
    offsetClass() {
      if (
        this.showReplyBox ||
        this.lastNonActivityMessageId === this.messageData.id
      ) {
        return '';
      }
      return this.isIncoming
        ? 'ltr:pr-[15%] rtl:pl-[15%]'
        : 'ltr:pl-[15%] rtl:pr-[15%]';
    },
    innerClass() {
      if (this.isIncoming) {
        const bgColor =
          this.isLastIncomingMessage && !this.isMessageDeleted
            ? 'bg-gradient'
            : 'bg-white';
        return this.isExpanded
          ? `border-green-500 ${bgColor}`
          : `border-gray-200 ${bgColor} ${
              this.isMessageDeleted
                ? ''
                : 'hover:bg-neutral-50 hover:border-neutral-300 cursor-pointer'
            }`;
      }
      return this.isExpanded
        ? 'bg-neutral-200 border-neutral-400'
        : `bg-neutral-200 border-neutral-300 ${
            this.isMessageDeleted
              ? ''
              : 'hover:border-neutral-400 cursor-pointer'
          }`;
    },
    receiverName() {
      const attrs = this.messageData?.content_attributes;
      if (this.isIncoming) {
        const toEmails = attrs?.email?.to || [];
        const inboxName = this.inbox?.business_name || this.inbox?.name;
        if (toEmails.length) {
          return toEmails.includes(this.inbox?.email) ? inboxName : toEmails[0];
        }
        return inboxName;
      }

      const toEmails = attrs?.to_emails.length
        ? attrs?.to_emails
        : [this.contact?.email];
      return toEmails.includes(this.contact?.email) || !toEmails.length
        ? this.contact?.name
        : toEmails[0];
    },
    receiverDetailTooltip() {
      const attrs = this.messageData?.content_attributes;
      if (this.isIncoming) {
        const email = attrs?.email;
        const toEmails = attrs?.email?.to || [];
        const toEmailsDisplayed = toEmails.length
          ? toEmails
          : [this.inbox?.email];
        return [
          {
            label: this.$t('EMAIL_HEADER.TO'),
            emails: toEmailsDisplayed.filter(Boolean),
          },
          { label: this.$t('EMAIL_HEADER.CC'), emails: email?.cc || [] },
          { label: this.$t('EMAIL_HEADER.BCC'), emails: email?.bcc || [] },
        ].filter(({ emails }) => emails.length);
      }

      const toEmails = attrs?.to_emails.length
        ? attrs?.to_emails
        : [this.contact?.email];
      return [
        { label: this.$t('EMAIL_HEADER.TO'), emails: toEmails.filter(Boolean) },
        { label: this.$t('EMAIL_HEADER.CC'), emails: attrs?.cc_emails || [] },
        { label: this.$t('EMAIL_HEADER.BCC'), emails: attrs?.bcc_emails || [] },
        ...(attrs?.forwarded
          ? [
              {
                label: this.$t('EMAIL_HEADER.FORWARD'),
                emails: attrs?.to_emails || [],
              },
            ]
          : []),
      ].filter(({ emails }) => emails.length);
    },
    sender() {
      const attrs = this.messageData?.content_attributes;
      const email = attrs?.email;
      if (email?.from) {
        return {
          email: email.from,
          name: email.from_original.replace(/"|<.*>/g, ''),
        };
      }
      if (this.isOutgoing) {
        return {
          email: this.inbox.email,
          name: this.messageData.sender?.name || this.inbox.email,
        };
      }
      return this.messageData.sender;
    },
    hourStamp() {
      return this.messageStampFull(this.messageData.created_at, 'HH:mm');
    },
    // d99d - same calculation as the WhatsApp bubble (bubble/Actions.vue)
    scheduledDate() {
      const { created_at: createdAt, delay_hours: delayHours } =
        this.messageData;
      if (!delayHours) return '';
      return this.messageStampFull(createdAt + delayHours * 3600 + 350);
    },
    attachments() {
      return this.messageData.attachments || [];
    },
    hasAttachment() {
      return !!this.attachments?.length;
    },
    plainTextContent() {
      const tmp = document.createElement('div');
      tmp.innerHTML = this.messageContent;
      tmp
        .querySelectorAll(
          'style, script, blockquote, .gmail_quote, .gmail_quote_container'
        )
        .forEach(el => el.remove());
      return (tmp.textContent || tmp.innerText || '').trim();
    },
    privateMode() {
      return REPLY_EDITOR_MODES.NOTE;
    },
    isPending() {
      return this.messageData.status === MESSAGE_STATUS.PROGRESS;
    },
    isFailed() {
      return this.messageData.status === MESSAGE_STATUS.FAILED;
    },
    isOutgoing() {
      return this.messageData.message_type === MESSAGE_TYPE.OUTGOING;
    },
    hasText() {
      return !!this.messageData.content;
    },
    contentAttributes() {
      return this.messageData.content_attributes || {};
    },
    isMessageDeleted() {
      return this.contentAttributes.deleted;
    },
    hasMediaAndPdfAttachment() {
      if (this.hasAttachment) {
        try {
          const { attachments = [{}] } = this.messageData;
          let type = attachments[0].file_type;
          if (type === 'file') {
            type = attachments[0].data_url.split('.').pop();
          }
          return ['image', 'video', 'audio', 'pdf'].includes(type);
        } catch (e) {
          return false;
        }
      }
      return false;
    },
    shouldShowContextMenu() {
      return !(this.isFailed || this.isPending || this.isMessageDeleted);
    },
    contextMenuEnabledOptions() {
      return {
        scopeTiktok: false,
        copy: this.hasText,
        delete:
          (this.hasText || this.hasAttachment) &&
          (this.deleteable || this.messageData.private) &&
          !this.messageData.content_attributes?.story_id &&
          this.hasInboxPermission('Inbox delete message'),
        cannedResponse:
          this.isOutgoing &&
          this.hasText &&
          this.hasCreatePermission('Canned Responses'),
        replyTo:
          !this.messageData.private &&
          this.inboxSupportsReplyTo &&
          this.hasInboxPermission('Inbox reply'),
        privateNote:
          !this.messageData.private &&
          this.hasInboxPermission('Inbox private notes'),
        remoteAssignFile: this.assignable && this.hasMediaAndPdfAttachment,
      };
    },
    isLastIncomingMessage() {
      return this.latestIncomingMessageId === this.messageData.id;
    },
    isLastNonActivityMessage() {
      return this.lastNonActivityMessageId === this.messageData.id;
    },
    externalError() {
      return this.contentAttributes.external_error || null;
    },
  },
  watch: {
    messageContent: {
      immediate: true,
      handler() {
        this.contentFullyHiddenByQuote = false;
        this.checkForFullyHiddenContent();
      },
    },
    allMessagesExpanded(value) {
      if (this.isLastNonActivityMessage) return;
      this.isExpanded = value;
    },
    replyingToMessageId(value) {
      this.showReplyBox = value === this.messageData.id;
    },
    isExpanded(value) {
      if (!value) this.showReplyBox = false;
      if (value) this.checkForFullyHiddenContent();
    },
    isMessageDeleted(value) {
      if (value) this.isExpanded = false;
    },
    async showContextMenu(val) {
      if (val) {
        if (!this.d99dFeatures.fb2Featureable) return;
        const { inbox_id, source_id } = this.messageData;
        if (this.d99dEnables.empty) {
          this.d99dEnables = {
            showLike: !this.contentAttributes.is_liked,
            showUnlike: this.contentAttributes.is_liked,
            showHide: this.isIncoming && !this.contentAttributes.is_hidden,
            showUnhide: this.isIncoming && this.contentAttributes.is_hidden,
          };
        } else if (this.d99dEnables.loading !== false) {
          this.d99dEnables = { loading: true };
        }
        const result = await this.$store.dispatch('getCommentInfo', {
          inbox_id,
          source_id,
        });
        const {
          user_likes: isLiked,
          can_like: canLike,
          can_hide: canHide,
          is_hidden: isHidden,
          can_reply_privately: canReplyPrivately,
        } = result;
        this.d99dEnables = {
          loading: false,
          showLike: canLike && !isLiked,
          showUnlike: canLike && isLiked,
          showHide: canHide && !isHidden,
          showUnhide: canHide && isHidden,
          showPrivateReply: canReplyPrivately,
        };
      }
    },
  },
  mounted() {
    this.isExpanded = this.isLastNonActivityMessage && !this.isMessageDeleted;
    bus.$on(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL, this.closeContextMenu);
  },
  beforeDestroy() {
    bus.$off(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL, this.closeContextMenu);
  },
  methods: {
    checkForFullyHiddenContent() {
      if (this.contentAttributes.forwarded || this.contentFullyHiddenByQuote) {
        return;
      }
      this.$nextTick(() => {
        const host = this.$refs.emailContentHost;
        if (!host || !host.shadowRoot) return;
        this.contentFullyHiddenByQuote = !host.innerText.trim();
      });
    },
    isRTL() {
      return this.$root.$i18n.locale === 'he';
    },
    openContextMenu(e) {
      const shouldSkipContextMenu =
        e.target?.classList.contains('skip-context-menu') ||
        e.target?.tagName.toLowerCase() === 'a';
      if (shouldSkipContextMenu || getSelection().toString()) return;
      e.preventDefault();
      if (e.type === 'contextmenu') {
        this.$track(ACCOUNT_EVENTS.OPEN_MESSAGE_CONTEXT_MENU);
      }
      this.contextMenuPosition = {
        x: e.pageX || e.clientX,
        y: e.pageY || e.clientY,
      };
      this.showContextMenu = true;
    },
    closeContextMenu() {
      this.showContextMenu = false;
      this.contextMenuPosition = { x: null, y: null };
    },
    handleRemoteAssignFile() {
      bus.$emit(BUS_EVENTS.SET_PLATFORM_ASSIGN_FILE, this.messageData);
    },
    async handleD99dSelection(option) {
      const { inbox_id, source_id } = this.messageData;
      const payload = { inbox_id, source_id };
      this.d99dEnables = {};
      try {
        switch (option) {
          case 'like':
            await this.$store.dispatch('createLike', payload);
            break;
          case 'unlike':
            await this.$store.dispatch('deleteLike', payload);
            break;
          case 'hide':
            await this.$store.dispatch('hide', payload);
            break;
          case 'unhide':
            await this.$store.dispatch('unhide', payload);
            break;
          case 'tiktokUnlike':
            payload.liked = false;
            await this.$store.dispatch('sendTiktokLike', payload);
            break;
          case 'tiktokLike':
            payload.liked = true;
            await this.$store.dispatch('sendTiktokLike', payload);
            break;
          case 'tiktokUnhide':
            payload.hide = false;
            await this.$store.dispatch('sendTiktokHide', payload);
            break;
          case 'tiktokHide':
            payload.hide = true;
            await this.$store.dispatch('sendTiktokHide', payload);
            break;
          case 'private_reply':
            this.d99dFeatures.handlePrivateReply(this.messageData);
            return;
          default:
            return;
        }
        this.showAlert(this.$t('CONVERSATION.SUCCESS_FB_COMMENT_UPDATE'));
      } catch (error) {
        this.showAlert(this.$t('CONVERSATION.FAIL_FB_COMMENT_UPDATE'));
      }
    },
    showPopoutReplyBox() {
      this.isPopoutReplyBox = !this.isPopoutReplyBox;
    },
    toggleExpandMesssage() {
      this.isExpanded = !this.isExpanded;
    },
    expandMessage() {
      if (this.isMessageDeleted) return;
      this.isExpanded = true;
    },
    handleForwardTo() {
      this.$emit('forward');
    },
    handleNote() {
      this.openReplyBox(REPLY_EDITOR_MODES.NOTE);
    },
    handleReplyTo() {
      this.openReplyBox(REPLY_EDITOR_MODES.REPLY);
    },
    handleReplyAll() {
      this.openReplyBox(REPLY_EDITOR_MODES.REPLY_ALL);
    },
    openReplyBox(mode) {
      const { conversation_id: conversationId, id: replyTo } = this.messageData;
      this.$store.dispatch('draftMessages/setReplyEditorMode', { mode });
      LocalStorage.updateJsonStore(
        LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO,
        conversationId,
        replyTo
      );
      LocalStorage.updateJsonStore(
        LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO,
        'mode',
        mode
      );
      this.showReplyBox = true;
      this.setReplyingToMessageId(this.messageData.id);
      this.$nextTick(() => {
        bus.$emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE);

        document
          .querySelector(`#message${this.messageData.id}`)
          .scrollIntoView({ block: 'end', behavior: 'smooth' });
      });
    },
    onClickSelfAssign() {
      const {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        avatar_url,
      } = this.currentUser;
      const selfAssign = {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        thumbnail: avatar_url,
      };
      this.$store.dispatch('setCurrentChatAssignee', selfAssign);
      this.$store
        .dispatch('assignAgent', {
          conversationId: this.currentChat.id,
          agentId: id,
        })
        .then(() => {
          this.showAlert(this.$t('CONVERSATION.CHANGE_AGENT'));
        })
        .catch(error => {
          this.showAlert(
            error?.response?.data?.error ||
              this.$t('CONVERSATION.CHANGE_AGENT_FAILED')
          );
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.bg-gradient {
  background: linear-gradient(98deg, #edfef3 -79.5%, #ffffff 62.6%);
}
</style>
